import { ArrowRight, Mail, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FadeIn, TextReveal } from "../components/Animations";
import { useState } from "react";

// Partners organized by hierarchy: Anchor → Government → Clinical → Knowledge
const partnerCategories = [
  {
    label: "Anchor Partners",
    partners: [
      { name: "Ministry of Health Kenya", logo: "https://customer-assets.emergentagent.com/job_maternal-wellness-9/artifacts/nj27pn14_image.png" },
      { name: "Jhpiego", logo: "https://customer-assets.emergentagent.com/job_maternal-wellness-9/artifacts/l0g2cwh6_image.png" },
    ]
  },
  {
    label: "Government Partners",
    partners: [
      { name: "Nakuru County Government", logo: "https://customer-assets.emergentagent.com/job_maternal-wellness-9/artifacts/3025qxaa_image.png" },
    ]
  },
  {
    label: "Clinical Partners",
    partners: [
      { name: "FIGO", logo: "https://www.figo.org/themes/custom/figo/logo.svg" },
      { name: "African Neonatal Association", logo: "https://africanneonatal.org/wp-content/uploads/2022/11/TPB7517_ANA-African-Neonatal-Association-Logo-.png" },
      { name: "Kenya Paediatric Association", logo: "https://kenyapaediatric.org/wp-content/uploads/2020/10/logo_mid.png" },
      { name: "Kenya Obstetrical & Gynaecological Society", logo: "https://customer-assets.emergentagent.com/job_maternal-wellness-9/artifacts/e40kt767_image.png" },
      { name: "Midwives Association of Kenya", logo: "https://customer-assets.emergentagent.com/job_maternal-wellness-9/artifacts/ipfg8s44_image.png" },
      { name: "National Nurses Association of Kenya", logo: "https://customer-assets.emergentagent.com/job_maternal-wellness-9/artifacts/3lsv6t5s_image.png" },
    ]
  },
  {
    label: "Knowledge Partners",
    partners: [
      { name: "AlignMNH", logo: "https://www.alignmnh.org/wp-content/themes/gesso/images/alignmnh-logo.svg" },
    ]
  },
];

// What it will take from all of us
const stakeholderGroups = [
  { 
    title: "Governments and Ministries of Health", 
    question: "Ready to deliver on national health goals?",
    desc: "A collective action platform that brings together impact, innovation, investment, and insight to accelerate the goals your country has already committed to. System readiness support, technical partnership, and coordinated financing working behind national priorities.",
  },
  { 
    title: "Innovators and Technologists", 
    question: "Have a solution ready for real health systems?",
    desc: "Navigate the pathway from evidence to national scale. Regulatory landscapes, clinical validation, procurement integration, and implementation support across the highest-burden geographies in Kenya, India, and the next wave of countries.",
  },
  { 
    title: "Investors and Donors", 
    question: "Looking for the highest return on investment in global health?",
    desc: "Financing architectures built on public investment at the core, designed to grow domestic resources and sustain outcomes well beyond the funding cycle. Join a community of like-minded partners putting catalytic capital behind two of the most measurable goals in global health.",
  },
  { 
    title: "Researchers and Academia", 
    question: "Want your evidence to shape practice?",
    desc: "Plug into country learning labs where your research directly informs what ministries fund, what clinicians adopt, and what gets scaled next.",
  },
  { 
    title: "Clinical and Professional Associations", 
    question: "Ready to raise the bar for newborn care?",
    desc: "Serve as technical partners and advisors shaping how innovations are introduced, how clinical protocols evolve, and how the next generation of the maternal and newborn health workforce is trained. The expertise your members carry is the foundation this platform is built on.",
  },
  { 
    title: "Implementers and NGOs", 
    question: "Ready to be part of what comes next?",
    desc: "Implement programs across the highest-burden geographies, learn alongside peers working on the same challenges across countries, and be part of a collective action platform where frontline experience directly shapes what gets scaled and how.",
  },
];

// Contact Form Component
function ContactFormSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    role: '',
    message: ''
  });
  const [status, setStatus] = useState('idle'); // idle, sending, success, error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', organization: '', role: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <section className="bg-[#F8F9FA] py-40 md:py-52">
      <div className="max-w-4xl mx-auto px-6">
        <FadeIn className="text-center mb-16">
          <p className="text-[#E8A33D] font-body text-[10px] tracking-[0.4em] uppercase mb-8 font-medium">
            Get in Touch
          </p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-[44px] font-semibold text-[#0A1628] mb-6 tracking-[-0.02em] leading-[1.15]">
            Let's Connect
          </h2>
          <p className="text-[#0A1628]/40 font-body text-base md:text-lg leading-relaxed max-w-xl mx-auto">
            Share where you stand today in making every birth a healthy birth and let's figure out together what the right next step looks like. Or reach out directly at{' '}
            <a 
              href="mailto:HealthyBirthInitiative@jhpiego.org" 
              className="text-[#E8A33D] hover:text-[#E8A33D]/80 transition-colors duration-300"
            >
              HealthyBirthInitiative@jhpiego.org
            </a>
          </p>
        </FadeIn>

        <FadeIn delay={0.2}>
          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-3xl shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-[#0A1628]/50 text-xs font-medium mb-2 tracking-wide uppercase">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-4 bg-[#F8F9FA] border-0 rounded-xl text-[#0A1628] text-sm placeholder:text-[#0A1628]/30 focus:outline-none focus:ring-2 focus:ring-[#E8A33D]/30 transition-all duration-300"
                  placeholder="Your name"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="block text-[#0A1628]/50 text-xs font-medium mb-2 tracking-wide uppercase">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-4 bg-[#F8F9FA] border-0 rounded-xl text-[#0A1628] text-sm placeholder:text-[#0A1628]/30 focus:outline-none focus:ring-2 focus:ring-[#E8A33D]/30 transition-all duration-300"
                  placeholder="your@email.com"
                  data-testid="contact-email"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-[#0A1628]/50 text-xs font-medium mb-2 tracking-wide uppercase">
                  Organization
                </label>
                <input
                  type="text"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  className="w-full px-4 py-4 bg-[#F8F9FA] border-0 rounded-xl text-[#0A1628] text-sm placeholder:text-[#0A1628]/30 focus:outline-none focus:ring-2 focus:ring-[#E8A33D]/30 transition-all duration-300"
                  placeholder="Your organization"
                  data-testid="contact-organization"
                />
              </div>
              <div>
                <label className="block text-[#0A1628]/50 text-xs font-medium mb-2 tracking-wide uppercase">
                  Your Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-4 bg-[#F8F9FA] border-0 rounded-xl text-[#0A1628] text-sm focus:outline-none focus:ring-2 focus:ring-[#E8A33D]/30 transition-all duration-300 appearance-none cursor-pointer"
                  data-testid="contact-role"
                >
                  <option value="">Select your role</option>
                  <option value="government">Government / Ministry of Health</option>
                  <option value="innovator">Innovator / Technologist</option>
                  <option value="investor">Investor / Donor</option>
                  <option value="researcher">Researcher / Academia</option>
                  <option value="clinical">Clinical / Professional Association</option>
                  <option value="implementer">Implementer / NGO</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            
            <div className="mb-8">
              <label className="block text-[#0A1628]/50 text-xs font-medium mb-2 tracking-wide uppercase">
                Message *
              </label>
              <textarea
                required
                rows={5}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-4 bg-[#F8F9FA] border-0 rounded-xl text-[#0A1628] text-sm placeholder:text-[#0A1628]/30 focus:outline-none focus:ring-2 focus:ring-[#E8A33D]/30 transition-all duration-300 resize-none"
                placeholder="Tell us how you'd like to get involved..."
                data-testid="contact-message"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-[#0A1628]/30 text-xs">
                <Mail size={12} className="inline mr-1" />
                Or email us directly at{' '}
                <a href="mailto:HealthyBirthInitiative@jhpiego.org" className="text-[#E8A33D]">
                  HealthyBirthInitiative@jhpiego.org
                </a>
              </p>
              
              <motion.button
                type="submit"
                disabled={status === 'sending'}
                className="inline-flex items-center gap-3 bg-[#0A1628] text-white px-8 py-4 text-sm font-medium rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: status === 'sending' ? 1 : 1.03 }}
                whileTap={{ scale: status === 'sending' ? 1 : 0.98 }}
                transition={{ duration: 0.3 }}
                data-testid="contact-submit"
              >
                {status === 'sending' ? 'Sending...' : 'Send Message'}
                <Send size={14} strokeWidth={1.5} />
              </motion.button>
            </div>
            
            {status === 'success' && (
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 text-green-600 text-sm text-center"
              >
                Thank you! We'll be in touch soon.
              </motion.p>
            )}
            
            {status === 'error' && (
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 text-red-500 text-sm text-center"
              >
                Something went wrong. Please email us directly at HealthyBirthInitiative@jhpiego.org
              </motion.p>
            )}
          </form>
        </FadeIn>
      </div>
    </section>
  );
}

export default function JoinPage() {
  return (
    <div data-testid="join-page" className="pt-16">
      {/* Hero Section - Spatial Luxury */}
      <section className="bg-white py-40 md:py-52 text-center">
        <div className="max-w-4xl mx-auto px-6">
          <FadeIn delay={0.2}>
            <p className="text-[#0A1628]/40 font-body text-base md:text-lg mb-8">
              The time to act alone has passed. The time to act together is now.
            </p>
          </FadeIn>
          
          <TextReveal delay={0.4}>
            <h1 data-testid="join-title" className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-[56px] font-semibold text-[#0A1628] leading-[1.1] tracking-[-0.02em] mb-12">
              Let's come together to make every birth a healthy birth.
            </h1>
          </TextReveal>
          
          <FadeIn delay={0.6}>
            <Link 
              to="/join"
              className="inline-flex items-center gap-3 bg-[#0A1628] text-white px-10 py-5 font-body text-sm font-medium rounded-full hover:bg-[#0F2744] transition-all duration-500"
            >
              Join the Initiative <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Partners Section - Symmetric rows with dotted containers */}
      <section className="bg-[#F8F9FA] py-32 md:py-40">
        <div className="max-w-6xl mx-auto px-6">
          <FadeIn>
            <p className="text-[#E8A33D] font-body text-[10px] tracking-[0.4em] uppercase mb-6 text-center font-medium">Collective Action in Practice</p>
          </FadeIn>
          <FadeIn delay={0.15}>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-[44px] font-semibold text-[#0A1628] mb-16 tracking-[-0.02em] text-center max-w-3xl mx-auto leading-[1.15]">
              Organizations contributing to this initiative
            </h2>
          </FadeIn>

          {/* Symmetric category rows */}
          <div className="space-y-4">
            {partnerCategories.map((category, catIndex) => (
              <FadeIn key={category.label} delay={0.1 + catIndex * 0.08}>
                <div className="bg-white rounded-2xl border-2 border-dashed border-[#0A1628]/8 p-5 md:p-6">
                  <div className="flex items-center gap-6 md:gap-8">
                    {/* Category label - fixed width */}
                    <div className="shrink-0 w-[140px] md:w-[160px]">
                      <span className="text-[10px] md:text-[11px] tracking-[0.12em] uppercase text-[#0A1628]/30 font-semibold">
                        {category.label}
                      </span>
                    </div>
                    {/* Vertical divider */}
                    <div className="shrink-0 w-px h-10 bg-[#0A1628]/8" />
                    {/* Partner logos - single line, no wrap */}
                    <div className="flex items-center gap-6 md:gap-8 overflow-x-auto flex-nowrap py-1">
                      {category.partners.map((partner, i) => (
                        <motion.div
                          key={partner.name}
                          className="shrink-0 flex items-center justify-center"
                          initial={{ opacity: 0, scale: 0.95 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: catIndex * 0.08 + i * 0.04, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                          whileHover={{ scale: 1.05 }}
                        >
                          {partner.logo ? (
                            <img 
                              src={partner.logo} 
                              alt={partner.name}
                              title={partner.name}
                              className="h-10 md:h-12 w-auto object-contain max-w-[120px] md:max-w-[150px] opacity-70 hover:opacity-100 transition-opacity duration-500"
                            />
                          ) : (
                            <div className="h-10 md:h-12 px-4 flex items-center justify-center bg-[#0A1628]/5 rounded-lg">
                              <span className="text-[#0A1628] font-heading font-bold text-[10px] tracking-wider whitespace-nowrap">{partner.abbr || partner.name}</span>
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* What It Will Take Section */}
      <section className="bg-white py-40 md:py-52">
        <div className="max-w-6xl mx-auto px-6">
          <FadeIn>
            <p className="text-[#E8A33D] font-body text-[10px] tracking-[0.4em] uppercase mb-8 font-medium">Join the Initiative</p>
          </FadeIn>
          <FadeIn delay={0.15}>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-[44px] font-semibold text-[#0A1628] mb-6 max-w-3xl tracking-[-0.02em] leading-[1.15]">
              What it will take from all of us
            </h2>
          </FadeIn>
          <FadeIn delay={0.3}>
            <p className="text-[#0A1628]/40 font-body text-base md:text-lg max-w-2xl mb-20 leading-relaxed">
              No single organization can deliver the change that is needed. It will take all of us—governments, clinicians, innovators, researchers, funders, and implementers—working together with shared purpose.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {stakeholderGroups.map((group, i) => (
              <FadeIn key={group.title} delay={i * 0.06}>
                <motion.div 
                  className="bg-[#F8F9FA] p-8 md:p-10 h-full flex flex-col group hover:bg-[#0A1628] transition-all duration-700 rounded-3xl"
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`stakeholder-card-${i}`}
                >
                  <h3 className="font-heading text-lg font-semibold text-[#0A1628] group-hover:text-white mb-2 tracking-tight transition-colors duration-700">{group.title}</h3>
                  <p className="text-[#E8A33D] font-body text-sm font-medium mb-4 italic transition-colors duration-700">{group.question}</p>
                  <p className="text-[#0A1628]/40 group-hover:text-white/50 font-body text-sm leading-relaxed mb-8 flex-1 transition-colors duration-700">{group.desc}</p>
                  <Link 
                    to="/join" 
                    className="inline-flex items-center gap-2 text-[#E8A33D] group-hover:text-[#E8A33D] font-body text-sm font-medium transition-all"
                  >
                    Get involved <ArrowRight size={14} strokeWidth={1.5} />
                  </Link>
                </motion.div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <ContactFormSection />

      {/* Hosted By / CTA - Single Focus */}
      <section className="bg-[#0A1628] py-40 md:py-52">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <FadeIn>
            <p className="text-white/25 font-body text-[10px] tracking-[0.4em] uppercase mb-10 font-medium">Secretariat</p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-[44px] font-semibold text-white mb-8 tracking-[-0.02em] leading-[1.15]">
              Hosted by <a href="https://www.jhpiego.org" target="_blank" rel="noopener noreferrer" className="text-[#E8A33D] hover:text-[#E8A33D]/80 transition-colors duration-500">Jhpiego</a>
            </h2>
          </FadeIn>
          <FadeIn delay={0.3}>
            <p className="text-white/30 font-body text-base md:text-lg mb-16 leading-relaxed max-w-2xl mx-auto">
              The initiative's secretariat is hosted by <a href="https://www.jhpiego.org" target="_blank" rel="noopener noreferrer" className="text-[#E8A33D]/80 hover:text-[#E8A33D] transition-colors duration-500">Jhpiego</a>, a Johns Hopkins University affiliate with over 50 years of experience bringing evidence-based health solutions to communities worldwide. Jhpiego coordinates the initiative's global operations and country support.
            </p>
          </FadeIn>
          <FadeIn delay={0.4}>
            <Link 
              to="/join" 
              data-testid="join-cta"
            >
              <motion.button
                className="inline-flex items-center gap-3 bg-white text-[#0A1628] px-10 py-5 text-sm font-body font-medium rounded-full"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.3 }}
              >
                Start a Conversation <ArrowRight size={16} strokeWidth={1.5} />
              </motion.button>
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
